import React from 'react';
import Markdown from 'markdown-to-jsx';
import { Box, Link } from '@mui/material';

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/**
 * URL policy for every link/image URL in an answer: only an absolute
 * http(s)/mailto URL survives. Anything else -- javascript:, data:,
 * relative paths, or a value the URL parser rejects -- is dropped, and the
 * link then renders as its plain text (see MarkdownLink below).
 */
const sanitizeUrl = (value) => {
  try {
    return SAFE_LINK_PROTOCOLS.has(new URL(value).protocol) ? value : null;
  } catch (err) {
    return null;
  }
};

/**
 * Source markers -- the RAG answer's [S1] / [S1, S3] form, plus the looser
 * bracketed forms the model has produced ([S1-S3], [S1 and S3], [S2 & S4];
 * the same set as fieldtime-rag-service's _PRIOR_SOURCE_LABEL_RE) -- are
 * citation text, never link syntax. They are escaped before parsing so none
 * can become (or be consumed by) a reference link, e.g. `[S1][S2]` alongside
 * an injected `[S2]: https://...` definition. Code shows text verbatim, so
 * MarkdownCode undoes the escape there.
 */
const MARKER_BODY = String.raw`\s*S\d+(?:\s*(?:,|and|&|-|–)\s*S\d+)*\s*`;
const SOURCE_MARKER = new RegExp(String.raw`\[(${MARKER_BODY})\]`, 'g');
const ESCAPED_SOURCE_MARKER = new RegExp(
  String.raw`\\\[(${MARKER_BODY})\\\]`,
  'g'
);

const escapeSourceMarkers = (content) =>
  content.replace(SOURCE_MARKER, '\\[$1\\]');

const MarkdownCode = ({ children, ...props }) => (
  <code {...props}>
    {React.Children.map(children, (child) =>
      typeof child === 'string'
        ? child.replace(ESCAPED_SOURCE_MARKER, '[$1]')
        : child
    )}
  </code>
);

const MarkdownLink = ({ href, children }) => {
  if (!href) {
    return <>{children}</>;
  }
  // Web links open in a new tab; mailto: hands off to the mail client.
  const newTab = !href.toLowerCase().startsWith('mailto:');
  return (
    <Link
      href={href}
      // Hovering always reveals the real destination; a Markdown link title
      // is ignored so it can't pose as a different URL.
      title={href}
      underline="always"
      {...(newTab && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </Link>
  );
};

// Images are never loaded: fetching a model-supplied image URL would let
// injected document text exfiltrate data without a click. Alt text only.
const MarkdownImage = ({ alt }) => alt || null;

// GFM task-list boxes are display-only (readonly alone still lets a click
// toggle a checkbox).
const MarkdownCheckbox = ({ checked }) => (
  <input type="checkbox" checked={Boolean(checked)} disabled readOnly />
);

// Every element the library gives a generated `id` (headings, and footnotes'
// footer/div) renders without it: model-controlled ids would collide across
// messages and could clobber DOM globals.
const idFreeOverrides = Object.fromEntries(
  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'footer', 'div'].map((Tag) => [
    Tag,
    // eslint-disable-next-line no-unused-vars
    ({ id, ...props }) => <Tag {...props} />,
  ])
);

const MARKDOWN_OPTIONS = {
  // Raw HTML in an answer stays literal text, never elements.
  disableParsingRawHTML: true,
  forceBlock: true,
  wrapper: React.Fragment,
  sanitizer: sanitizeUrl,
  overrides: {
    a: MarkdownLink,
    code: MarkdownCode,
    img: MarkdownImage,
    input: MarkdownCheckbox,
    ...idFreeOverrides,
  },
};

// Sized for the chat bubble's 13px body text; colors from ChatPanel's palette.
const MARKDOWN_SX = {
  fontSize: 13,
  lineHeight: 1.43, // MUI body2, as the plain-text bubble used
  overflowWrap: 'anywhere',
  '& > :first-child': { mt: 0 },
  '& > :last-child': { mb: 0 },
  // pre-line keeps single line breaks visible, as the plain-text bubble did.
  '& p, & li': { whiteSpace: 'pre-line' },
  '& p': { my: 0.75 },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    mt: 1.25,
    mb: 0.5,
    fontWeight: 700,
    lineHeight: 1.3,
    fontSize: 13,
  },
  '& h1': { fontSize: 16 },
  '& h2': { fontSize: 15 },
  '& h3': { fontSize: 14 },
  '& ul, & ol': { my: 0.75, pl: 2.5 },
  '& li': { my: 0.25 },
  '& li > p': { my: 0 },
  '& li > p + p': { mt: 0.75 },
  '& table': {
    display: 'block',
    maxWidth: '100%',
    overflowX: 'auto',
    borderCollapse: 'collapse',
    my: 1,
    fontSize: 12,
  },
  '& th, & td': {
    border: '1px solid #e2e5ea',
    px: 1,
    py: 0.5,
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': { bgcolor: '#f5f6f8', fontWeight: 600 },
  // Font family comes from index.css's global `code` rule.
  '& code': {
    fontSize: 12,
    bgcolor: '#f5f6f8',
    px: 0.5,
    borderRadius: 0.5,
  },
  '& pre': {
    my: 0.75,
    p: 1,
    bgcolor: '#f5f6f8',
    borderRadius: 1,
    overflowX: 'auto',
    '& code': { p: 0 },
  },
  '& hr': { my: 1, border: 0, borderTop: '1px solid #e2e5ea' },
  '& footer': {
    mt: 1,
    pt: 0.75,
    borderTop: '1px solid #e2e5ea',
    fontSize: 12,
    color: '#5c6478',
  },
  '& blockquote': {
    my: 0.75,
    mx: 0,
    pl: 1.5,
    borderLeft: '3px solid #e2e5ea',
    color: '#5c6478',
  },
};

/**
 * Renders one assistant answer's Markdown (bold, italics, headings, lists,
 * tables, links) as React elements -- never as HTML strings. Inline source
 * markers such as [S1] stay literal text; citation chips are rendered
 * separately by ChatPanel's MessageBubble. Memoized so typing in the
 * composer doesn't re-parse every answer.
 */
const AssistantMarkdown = React.memo(({ content }) => (
  <Box sx={MARKDOWN_SX}>
    <Markdown options={MARKDOWN_OPTIONS}>
      {escapeSourceMarkers(content || '')}
    </Markdown>
  </Box>
));

export default AssistantMarkdown;
